/**
 * SABA Wishlist Products auto-mount
 * ---------------------------------------------------------------------
 * On any page where `window.sabaWishlistItems` is present (an array of
 * wishlist items, each with `variantId` + `product.handle`), this script
 * auto-creates a <section class="saba-wishlist-products"> inside <main> and
 * stamps a server-rendered product-card - plus the drawers that card's
 * controls target - into it for each item.
 *
 * Each card is fetched via the `saba-wishlist-card` section endpoint with
 * the saved variant id forwarded as `?variant=`, so the card reflects the
 * variant the customer saved (Shopify resolves `?variant=` into
 * `product.selected_or_first_available_variant`).
 *
 * Companion section: sections/saba-wishlist-card.liquid
 * ---------------------------------------------------------------------
 */
/* ── Producer: publish window.sabaWishlistItems from the Wishlist King API ──
 * The consumer below only READS window.sabaWishlistItems (QA seeds it directly
 * via the documented contract). In production nothing ever published it: JAG
 * runs the app's native grid, so the theme-cards path never had a live feed.
 * This block fills it from the app's own API using the page's #wkAppSettings
 * config (apiHost + storefront token) and the identity headers the SDK
 * itself sends (x-appmate-sid for a logged-out session, x-appmate-cid for a
 * signed-in customer - localStorage.wk_session_id only ever exists after a
 * logged-out add, so it is the fallback, not the source), then re-publishes
 * on the app's documented success events.
 * The wishlist API returns productId/variantId only, so handles resolve
 * through the Storefront API token the app already exposes on the page. */
(function () {
  'use strict';
  if (window.__sabaWishlistProducer) return;
  window.__sabaWishlistProducer = true;

  function cfg() {
    try {
      return JSON.parse(document.getElementById('wkAppSettings').textContent).config;
    } catch (_) {
      return null;
    }
  }

  async function handlesFor(c, ids) {
    const res = await fetch(
      'https://' + c.shopPermanentDomain + '/api/' + c.storefrontVersion + '/graphql.json',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': c.storefrontAccessToken,
        },
        body: JSON.stringify({
          query: 'query($ids:[ID!]!){nodes(ids:$ids){... on Product{id handle}}}',
          variables: { ids: ids.map((id) => 'gid://shopify/Product/' + id) },
        }),
      }
    );
    if (!res.ok) throw new Error('storefront ' + res.status);
    const data = (await res.json()).data;
    const map = {};
    ((data && data.nodes) || []).forEach((n) => {
      if (n) map[n.id.split('/').pop()] = n.handle;
    });
    return map;
  }

  function onWishlistPage() {
    // The consumer's documented contract mounts wherever the items array
    // exists (QA seeds rely on that), so the PRODUCER carries the page gate:
    // publish only on the app's own wishlist path, or the grid + chrome would
    // stamp above the footer on every page.
    let path = '/apps/wishlist';
    try {
      path = JSON.parse(document.getElementById('wkAppSettings').textContent).settings.general.wishlistPath || path;
    } catch (_) {}
    return window.location.pathname.indexOf(path) === 0;
  }

  function identityHeaders(c) {
    const wk = window.WishlistKing;
    const sdk = wk && wk._apis && wk._apis.wishlist && wk._apis.wishlist.headers;
    if (sdk && (sdk['x-appmate-sid'] || sdk['x-appmate-cid'])) {
      // A signed-in customer's list only comes back with the customer header;
      // publishing on the session header first shows the anonymous (empty)
      // list for a moment before the real one lands.
      if (c.customerId && !sdk['x-appmate-cid']) return null;
      return Object.assign({}, sdk);
    }
    if (c.customerId) return null;
    const sid = localStorage.getItem('wk_session_id');
    if (sid) return { 'x-appmate-shp': c.shopPermanentDomain, 'x-appmate-sid': sid };
    return null;
  }

  // Resolves false when the app has not booted yet (no settings or no
  // identity), so schedule() can retry; true once the snapshot is published.
  async function publish() {
    if (!onWishlistPage()) return true;
    const c = cfg();
    const headers = c && identityHeaders(c);
    if (!headers) return false;
    const res = await fetch(c.apiHost + '/wishlists/mine', { headers: headers });
    if (!res.ok) return true;
    const wishlist = ((await res.json()) || {}).wishlist || {};
    // Stashed for the share handler below - shareWishlist needs publicId.
    window.__sabaWishlist = wishlist;
    const items = wishlist.items || [];
    const map = items.length
      ? await handlesFor(c, Array.from(new Set(items.map((i) => i.productId))))
      : {};
    window.sabaWishlistItems = items
      .filter((i) => map[String(i.productId)])
      .map((i) => ({
        id: i.id,
        productId: i.productId,
        product: { handle: map[String(i.productId)] },
        variantId: i.variantId,
      }));
    return true;
  }

  // The SDK and #wkAppSettings arrive after this deferred script, so the
  // first publish usually finds nothing: retry for up to 15s.
  let retries = 0;
  function schedule() {
    publish()
      .then(function (done) {
        if (done === false && retries < 30) {
          retries++;
          setTimeout(schedule, 500);
        }
      })
      .catch(function () {});
  }
  // The API can still return a just-removed (or omit a just-added) item for
  // a moment after the app reports success; refetching straight away
  // publishes that stale list and the grid re-renders twice.
  const EVENT_REFETCH_DELAY_MS = 1000;
  let eventTimer = null;
  function scheduleAfterEvent() {
    clearTimeout(eventTimer);
    eventTimer = setTimeout(schedule, EVENT_REFETCH_DELAY_MS);
  }
  ['wk:wishlist:add:success', 'wk:wishlist:remove:success', 'wk:wishlist:update:success', 'wk:wishlist:change-variant:success'].forEach(
    function (ev) {
      document.addEventListener(ev, scheduleAfterEvent);
    }
  );
  // Initial publish - never clobber a pre-seeded array (the QA contract).
  if (!Array.isArray(window.sabaWishlistItems)) schedule();
})();

(function () {
  const SECTION_ID = 'saba-wishlist-card';
  const HEADER_SECTION_ID = 'saba-wishlist-header';
  const EMPTY_SECTION_ID = 'saba-wishlist-empty';
  const READY_EVENT = 'saba:wishlist-items-ready';
  const CONTAINER_CLASS = 'saba-wishlist-products';
  const HEADER_CLASS = 'saba-wishlist-header';
  const EMPTY_CLASS = 'saba-wishlist-empty';
  const BREADCRUMBS_CLASS = 'saba-wishlist-breadcrumbs';
  // Shared leading segment of every drawer id the card markup emits - both the
  // wishlist drawer and its per-panel notify-me drawers (see the drawer_id
  // capture in snippets/product-card.liquid).
  const DRAWER_ID_PREFIX = 'drawer-card-wishlist-';
  // The share drawer stamps with the header chrome, not the cards, so it gets
  // its own prefix registration in the same open-drawer guard below.
  const SHARE_DRAWER_ID_PREFIX = 'drawer-wishlist-share';

  // Signature of the last-rendered items list. We re-render whenever the
  // signature changes - covers reassignment (window.sabaWishlistItems = …)
  // and in-place mutation (push/splice). null = no render yet.
  let lastSignature = null;
  let renderInFlight = false;

  // Ids removed from this page are dropped from every list write for a short
  // window: the card leaves at once on the click, and a stale refetch (the
  // API can still return a just-removed item) cannot bring it back.
  const SUPPRESS_MS = 10000;
  const suppressed = new Map();

  // Keyed by item id and by variant id: a first add carries a temp- id until
  // the API's id lands, so the variant is the stable key across that swap.
  function suppress(id, variantId) {
    const now = Date.now();
    if (id) suppressed.set(String(id), now);
    if (variantId) suppressed.set('v:' + String(variantId), now);
  }

  function liveItems() {
    const items = window.sabaWishlistItems;
    if (!Array.isArray(items)) return null;
    const now = Date.now();
    suppressed.forEach((t, id) => {
      if (now - t > SUPPRESS_MS) suppressed.delete(id);
    });
    if (!suppressed.size) return items;
    return items.filter(
      (i) => !(i && ((i.id && suppressed.has(String(i.id))) || (i.variantId && suppressed.has('v:' + String(i.variantId)))))
    );
  }

  function getSignature() {
    const items = liveItems();
    if (!items) return null;
    // Ids are deliberately left out: the app seeds a fresh add with a temp-
    // id and the API's id lands a moment later, and re-rendering for that
    // swap replaced the card under the customer's click. syncCardIds() writes
    // the current id onto the stamped card in place instead.
    return items
      .map((i) => `${(i && i.product && i.product.handle) || ''}:${(i && i.variantId) || ''}`)
      .join('|');
  }

  function getMain() {
    return document.getElementById('main') || document.querySelector('main');
  }

  function ensureContainer() {
    let section = document.querySelector(`section.${CONTAINER_CLASS}`);
    if (section) {
      // `:scope >` is load-bearing: the empty-state chrome stamps its OWN
      // product-list (the carousel) inside this section, so an unscoped
      // lookup resolves to the carousel on an empty -> populated flip and the
      // cards get stamped into chrome that removeChrome then discards.
      return section.querySelector(':scope > product-list');
    }

    const main = getMain();
    if (!main) return null;

    section = document.createElement('section');
    section.className = CONTAINER_CLASS;

    // Wrap cards in a <product-list> so the theme's existing grid styles
    // (gap, breakpoints, --product-list-column-count) apply automatically.
    const list = document.createElement('product-list');
    list.className = 'product-list';
    section.appendChild(list);

    main.appendChild(section);
    return list;
  }

  // Page chrome (heading + action row above the grid, breadcrumb below it).
  // The app-proxy page carries no theme sections, so the chrome comes from the
  // same section-endpoint pattern as the cards - one fetch for both fragments.
  // Populated state: sections/saba-wishlist-header.liquid.
  // Empty state:    sections/saba-wishlist-empty.liquid.
  let chromeStamped = false;

  // The empty carousel's collection is the theme setting
  // settings.saba_wishlist_empty_collection (falls back to All products).
  // The Section Rendering API shape is `?sections=<id>` returning
  // `{ "<id>": "<html>" }`. The lineage `sections/<id>.json` path 404s on every
  // surface (dev proxy AND authenticated storefront), so the chrome never
  // stamped -- this served the POPULATED header too, not just the empty state.
  async function fetchSectionDoc(sectionId) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url = new URL(root, window.location.origin);
    url.searchParams.set('sections', sectionId);
    try {
      const response = await fetch(url.toString());
      if (!response.ok) throw new Error('chrome fetch failed');
      const payload = await response.json();
      const html = typeof payload === 'string' ? payload : payload[sectionId];
      if (!html) throw new Error('chrome fragment missing');
      return new DOMParser().parseFromString(html, 'text/html');
    } catch (_) {
      return null;
    }
  }

  // empty=true stamps the empty-state chrome (heading + CTA row, carousel);
  // empty=false stamps the populated chrome - the pre-existing behaviour,
  // unchanged except that the fragment lookup comes through fetchSectionDoc.
  async function stampChrome(section, empty) {
    if (chromeStamped || !section) return;
    // Claim the slot before awaiting so a second render() can't double-stamp.
    chromeStamped = true;

    const doc = await fetchSectionDoc(empty ? EMPTY_SECTION_ID : HEADER_SECTION_ID);
    if (!doc) {
      // The grid is the page; chrome is additive, so a failure here is silent.
      chromeStamped = false;
      return;
    }

    const chromeClass = empty ? EMPTY_CLASS : HEADER_CLASS;
    const header = doc.querySelector(`.${chromeClass}`);
    if (header && !section.querySelector(`.${chromeClass}`)) {
      section.insertBefore(document.importNode(header, true), section.firstChild);
    }

    // The breadcrumb sits after the section so the section's own bottom padding
    // supplies the design's 40/80 gap above it (Figma '.Section Padding').
    const crumbs = doc.querySelector(`.${BREADCRUMBS_CLASS}`);
    if (crumbs && !document.querySelector(`.${BREADCRUMBS_CLASS}`)) {
      section.insertAdjacentElement('afterend', document.importNode(crumbs, true));
    }

    // The desktop share drawer ships in the same section response (the page is
    // an app proxy -- one fetch carries trigger + drawer). It sits outside the
    // section so the fixed panel never inherits section transforms, and it
    // survives the container wipes below (the open-drawer guard defers those).
    const shareDrawer = doc.querySelector(`#${SHARE_DRAWER_ID_PREFIX}`);
    if (shareDrawer && !document.getElementById(SHARE_DRAWER_ID_PREFIX)) {
      document.body.appendChild(document.importNode(shareDrawer, true));
    }
  }

  // Merchant content for the wishlist page lives on the theme page with handle
  // "wishlist-setup" (the app-proxy page cannot hold sections). Each section's
  // "Wishlist page visibility" marker decides which state it shows in.
  const SETUP_PAGE_HANDLE = 'wishlist-setup';
  const SETUP_CLASS = 'saba-wishlist-setup-sections';
  let setupIndexPromise = null;
  let setupMountToken = 0;

  function setupPageUrl() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return new URL(`${root}pages/${SETUP_PAGE_HANDLE}`, window.location.origin);
  }

  function loadSetupIndex() {
    if (!setupIndexPromise) {
      setupIndexPromise = fetch(setupPageUrl().toString())
        .then((response) => (response.ok ? response.text() : ''))
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const main = doc.getElementById('main') || doc.querySelector('main') || doc.body;
          return Array.from(main.querySelectorAll('.shopify-section'))
            .filter((el) => !el.classList.contains('shopify-section--main-page'))
            .map((el) => {
              const marker = el.querySelector('[data-wishlist-visibility]');
              return {
                id: el.id.replace(/^shopify-section-/, ''),
                visibility: (marker && marker.getAttribute('data-wishlist-visibility')) || 'both',
              };
            })
            .filter((entry) => entry.id);
        })
        .catch(() => []);
    }
    return setupIndexPromise;
  }

  async function mountSetupSections(section, state) {
    if (!section) return;
    const token = ++setupMountToken;
    removeSetupSections();
    const index = await loadSetupIndex();
    const wanted = index.filter((entry) => entry.visibility === 'both' || entry.visibility === state);
    if (!wanted.length) return;
    const nodes = await Promise.all(
      wanted.map(async (entry) => {
        try {
          const url = setupPageUrl();
          url.searchParams.set('section_id', entry.id);
          const response = await fetch(url.toString());
          if (!response.ok) return null;
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const node = doc.querySelector('.shopify-section') || doc.body.firstElementChild;
          // A section with nothing to show (e.g. an empty collection) stays off the page.
          if (!node || !node.firstElementChild || !node.textContent.trim()) return null;
          return node;
        } catch (_) {
          return null;
        }
      })
    );
    // A newer render (state change) owns the slot now.
    if (token !== setupMountToken) return;
    removeSetupSections();
    const wrap = document.createElement('div');
    wrap.className = SETUP_CLASS;
    wrap.dataset.wishlistSetup = state;
    nodes.filter(Boolean).forEach((node) => wrap.appendChild(document.importNode(node, true)));
    if (!wrap.childElementCount) return;
    const slot = state === 'empty' ? section.querySelector('[data-wishlist-suggestions]') : null;
    (slot || section).appendChild(wrap);
  }

  function removeSetupSections() {
    document.querySelectorAll(`.${SETUP_CLASS}`).forEach((node) => node.remove());
  }

  function removeChrome(section) {
    chromeStamped = false;
    removeSetupSections();
    const header = section && section.querySelector(`.${HEADER_CLASS}, .${EMPTY_CLASS}`);
    if (header) header.remove();
    const crumbs = document.querySelector(`.${BREADCRUMBS_CLASS}`);
    if (crumbs) crumbs.remove();
    // The share drawer is only meaningful next to the populated chrome -- an
    // emptied wishlist removes the trigger, so the drawer goes with it.
    const shareDrawer = document.getElementById(SHARE_DRAWER_ID_PREFIX);
    if (shareDrawer) shareDrawer.remove();
  }

  async function fetchCard(item) {
    const handle = item && item.product && item.product.handle;
    const variantId = item && item.variantId;
    if (!handle) return null;

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url = new URL(`${root}products/${handle}`, window.location.origin);
    if (variantId) url.searchParams.set('variant', String(variantId));
    url.searchParams.set('section_id', SECTION_ID);

    try {
      const response = await fetch(url.toString());
      if (!response.ok) return null;
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const card = doc.querySelector('product-card');
      if (!card) return null;
      // The card's "Save to wishlist" and "Notify me" drawers are rendered as
      // SIBLINGS of <product-card>, not inside it - see the tail of
      // snippets/product-card.liquid, where they are deliberately kept out of
      // the card's anchor wrapper. The trigger button resolves its drawer by
      // id, so importing the card on its own leaves the control pointing at
      // nothing. Closed drawers compute to display:none, so they occupy no
      // slot in the grid they land in (same shape as the PLP markup).
      const drawers = Array.from(card.parentElement.querySelectorAll(':scope > x-drawer'));
      // The remove control and the add-to-cart hand-off resolve the saved item by this id.
      card.dataset.wishlistItemId = item.id || '';
      return [card, ...drawers].map((node) => document.importNode(node, true));
    } catch (_) {
      return null;
    }
  }

  // All product cards on the SABA storefront share the same colour scheme,
  // so reading the first card's scheme class and applying it to the whole
  // section is sufficient - there's no scenario where two cards in the
  // same wishlist would resolve to different schemes.
  function applyCardColorScheme(section, card) {
    if (!section || !card) return;
    const schemeClass = Array.from(card.classList).find((c) => c.startsWith('color-scheme--'));
    if (!schemeClass) return;
    // Strip any previous scheme so this is idempotent across refreshes.
    Array.from(section.classList)
      .filter((c) => c.startsWith('color-scheme--'))
      .forEach((c) => section.classList.remove(c));
    section.classList.add('color-scheme', schemeClass);
  }

  // Cap concurrent PDP fetches so a 50-item wishlist doesn't fire 50
  // simultaneous requests at the storefront.
  const FETCH_CONCURRENCY = 4;

  async function fetchAllCards(items) {
    const results = new Array(items.length);
    let cursor = 0;
    const workers = Array.from({ length: Math.min(FETCH_CONCURRENCY, items.length) }, async () => {
      while (true) {
        const i = cursor++;
        if (i >= items.length) return;
        results[i] = await fetchCard(items[i]);
      }
    });
    await Promise.all(workers);
    return results;
  }

  // While a stamped drawer is open the dialog element has moved it to <body>,
  // restoring it to this container only on close. Wiping the container
  // underneath it would strand that node in <body> alongside a freshly stamped
  // copy of the same id, so a re-render has to wait the drawer out. This is a
  // live path, not a corner: the drawer's own "Add to Wishlist" CTA changes the
  // item list while the drawer is still open.
  function hasOpenStampedDrawer() {
    return (
      document.querySelector(`x-drawer[id^="${DRAWER_ID_PREFIX}"][open]`) !== null ||
      document.querySelector(`x-drawer[id^="${SHARE_DRAWER_ID_PREFIX}"][open]`) !== null
    );
  }

  // The empty <-> populated swap wipes the section and refills it over several
  // fetches; holding the outgoing height keeps the page from collapsing and
  // regrowing under the reader.
  function holdHeight(section) {
    if (!section) return () => {};
    const height = section.getBoundingClientRect().height;
    if (height) section.style.minHeight = `${Math.round(height)}px`;
    return () => {
      section.style.minHeight = '';
    };
  }

  async function render() {
    if (renderInFlight) {
      // Same release as the open-drawer path below. tick() has already written
      // this signature to lastSignature before calling us, so returning without
      // clearing it would record the change as applied and drop it: nothing
      // re-attempts until the list changes again. The in-flight pass may be
      // fetching the previous list, so the next tick has to run.
      lastSignature = null;
      return;
    }
    const items = liveItems();
    if (!items) return;

    if (hasOpenStampedDrawer()) {
      // Release the signature so the next poll tick re-attempts this render
      // instead of treating the change as already applied.
      lastSignature = null;
      return;
    }

    const container = ensureContainer();
    if (!container) return;
    const section = container.closest(`section.${CONTAINER_CLASS}`);

    async function showEmpty() {
      const release = holdHeight(section);
      container.replaceChildren();
      // The empty state carries its own chrome (heading + CTA, carousel,
      // breadcrumb - sections/saba-wishlist-empty.liquid), so the
      // populated header goes with the cards and the empty fragment is
      // stamped in its place. Wishlist King's own empty markup is suppressed
      // by the wishlist-page CSS (see the wishlist header/empty block in
      // assets/saba.css).
      removeChrome(section);
      renderInFlight = true;
      try {
        await stampChrome(section, true);
        await mountSetupSections(section, 'empty');
      } finally {
        renderInFlight = false;
        release();
      }
    }

    if (items.length === 0) {
      await showEmpty();
      return;
    }

    const release = holdHeight(section);
    renderInFlight = true;
    try {
      const cards = await fetchAllCards(items);
      // Each fetch yields [card, ...its drawers]. Flatten into one stamp list,
      // skipping any drawer id already claimed in this pass - two saved
      // variants of the SAME product resolve to the same drawer ids, and a
      // duplicate id would leave the second card's trigger opening the first
      // card's drawer. replaceChildren covers the re-render case by wiping the
      // previous stamp wholesale.
      const claimed = new Set();
      const nodes = [];
      let firstCard = null;
      cards.filter(Boolean).forEach((group) => {
        group.forEach((node) => {
          if (node.id) {
            if (claimed.has(node.id)) return;
            claimed.add(node.id);
          }
          if (!firstCard && node.matches('product-card')) firstCard = node;
          nodes.push(node);
        });
      });
      if (!firstCard) {
        // Every saved item points at a product this store cannot render (for
        // example unpublished here), so the page reads as empty. A standing
        // empty state is left as it is rather than rebuilt.
        renderInFlight = false;
        release();
        if (!section.querySelector(`.${EMPTY_CLASS}`)) await showEmpty();
        return;
      }
      // Only the empty chrome has to go when items arrive; a standing populated
      // header stays put so a re-render does not pull it out and refetch it.
      if (section.querySelector(`.${EMPTY_CLASS}`)) removeChrome(section);
      container.replaceChildren(...nodes);
      runCardScripts(container);
      applyCardColorScheme(section, firstCard);
      await stampChrome(section, false);
      await mountSetupSections(section, 'filled');
    } finally {
      renderInFlight = false;
      release();
    }
  }

  // Continuous reactivity: poll for changes to window.sabaWishlistItems and
  // re-render whenever the contents change. Polling (rather than a setter
  // trap) is intentional - it works regardless of how the wishlist provider
  // mutates the array (reassignment, push/splice, Proxy/Vue store, etc.).
  // Paused while the tab is hidden so we don't burn CPU on background tabs.
  const POLL_INTERVAL_MS = 500;
  let pollTimer = null;

  // Keeps each stamped card's item id current (temp- to real) without touching
  // the DOM node, so a click during that swap still resolves the real id.
  function syncCardIds() {
    const items = liveItems();
    if (!items || !items.length) return;
    document.querySelectorAll(`.${CONTAINER_CLASS} > product-list product-card`).forEach((card) => {
      const input = card.querySelector('form input[name="id"]');
      const variantId = input && input.value;
      if (!variantId) return;
      const match = items.find((i) => i && i.id && String(i.id).indexOf('temp-') !== 0 && String(i.variantId) === String(variantId));
      if (match && card.dataset.wishlistItemId !== String(match.id)) card.dataset.wishlistItemId = String(match.id);
    });
  }

  function tick() {
    const sig = getSignature();
    if (sig === null) return; // global not present yet
    syncCardIds();
    if (sig === lastSignature) return; // unchanged
    lastSignature = sig;
    render();
  }

  function startPolling() {
    if (pollTimer !== null) return;
    pollTimer = setInterval(tick, POLL_INTERVAL_MS);
  }

  function stopPolling() {
    if (pollTimer === null) return;
    clearInterval(pollTimer);
    pollTimer = null;
  }

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      stopPolling();
    } else {
      // Catch up on any change that happened while hidden, then resume.
      tick();
      startPolling();
    }
  });

  if (!document.hidden) startPolling();
  // Run an immediate first tick so the page doesn't wait the full interval
  // when the global is already populated.
  tick();

  // Explicit signals from the wishlist provider - both bypass the polling
  // delay and force an immediate re-render.
  document.addEventListener(READY_EVENT, () => {
    lastSignature = null;
    tick();
  });

  window.sabaWishlistRefresh = () => {
    lastSignature = null;
    tick();
  };

  // importNode never runs a card's inline scripts. The size-bar toggle they
  // carry is needed for the fallback card (item saved without a size), so each
  // distinct script is replayed once; the scripts guard their own re-runs.
  const replayedScripts = new Set();
  function runCardScripts(container) {
    container.querySelectorAll('product-card script:not([type]), product-card script[type="text/javascript"]').forEach((script) => {
      const code = script.textContent;
      if (!code || replayedScripts.has(code)) return;
      replayedScripts.add(code);
      const clone = document.createElement('script');
      clone.textContent = code;
      document.body.appendChild(clone);
      clone.remove();
    });
  }

  // The app fires wk:wishlist:remove:success, the producer republishes the
  // items array, and the poll tick re-renders the grid.
  function savedItemId(card) {
    const stamped = card.dataset.wishlistItemId;
    if (stamped && stamped.indexOf('temp-') !== 0) return stamped;
    // Fall back to the current snapshot by variant, then by product.
    const input = card.querySelector('form input[name="id"]');
    const variantId = input && input.value;
    const productId = card.querySelector('form input[name="product-id"]');
    const items = (window.__sabaWishlist && window.__sabaWishlist.items) || window.sabaWishlistItems || [];
    const real = (i) => i && i.id && String(i.id).indexOf('temp-') !== 0;
    const match =
      items.find((i) => real(i) && variantId && String(i.variantId) === String(variantId)) ||
      items.find((i) => real(i) && productId && String(i.productId) === String(productId.value));
    return match ? match.id : stamped;
  }

  function removeSavedItem(card) {
    const wk = window.WishlistKing;
    const id = card && savedItemId(card);
    if (!id || !wk || typeof wk.removeWishlistItem !== 'function') return;
    // Already on its way out (the add path can reach here twice).
    if (suppressed.has(String(id))) return;
    // Optimistic: the card leaves now and the list re-renders without it;
    // the API call completes in the background.
    const variantInput = card.querySelector('form input[name="id"]');
    const variantId = variantInput && variantInput.value;
    suppress(id, variantId);
    lastSignature = null;
    tick();
    const unsuppress = () => {
      suppressed.delete(String(id));
      if (variantId) suppressed.delete('v:' + String(variantId));
      lastSignature = null;
      tick();
    };
    // A card stamped from the app's optimistic add carries a temp- id that
    // the API cannot remove; wait for the real id to land (matched by variant)
    // before calling it, while the card stays hidden.
    const realId = (currentId) => {
      if (currentId && String(currentId).indexOf('temp-') !== 0) return currentId;
      const items = ((window.__sabaWishlist && window.__sabaWishlist.items) || []).concat(
        window.sabaWishlistItems || []
      );
      const match = items.find(
        (i) => i && i.id && String(i.id).indexOf('temp-') !== 0 && variantId && String(i.variantId) === String(variantId)
      );
      return match ? match.id : null;
    };
    let tries = 0;
    const attempt = () => {
      const resolved = realId(id);
      if (!resolved) {
        if (tries++ < 20) return setTimeout(attempt, 500);
        return unsuppress();
      }
      wk.removeWishlistItem({ wishlistItemId: resolved }).catch(unsuppress);
    };
    attempt();
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('.saba-wishlist-card__remove');
    if (!button) return;
    event.preventDefault();
    removeSavedItem(button.closest('product-card'));
  });

  // Added to cart from a wishlist card: the item leaves the wishlist, as on JAG.
  document.addEventListener('variant:add', (event) => {
    const target = event.target;
    const card = target && typeof target.closest === 'function' ? target.closest(`.${CONTAINER_CLASS} product-card`) : null;
    if (card) removeSavedItem(card);
  });

  // The theme only fires variant:add when the cart request succeeds; a variant
  // already at its stock cap in the cart is refused, so the click is remembered
  // and the cart is checked once it settles: if the variant is in the cart,
  // the saved item leaves. A refused add that puts nothing in the cart keeps it.
  const PENDING_ADD_MS = 15000;
  const pendingAdds = new Map();

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      const card = form && typeof form.closest === 'function' ? form.closest(`.${CONTAINER_CLASS} product-card`) : null;
      const input = card && form.querySelector('input[name="id"]');
      if (input && input.value) pendingAdds.set(String(input.value), Date.now());
    },
    true
  );

  async function settlePendingAdds() {
    if (!pendingAdds.size) return;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    let cart;
    try {
      cart = await (await fetch(`${root}cart.js`)).json();
    } catch (_) {
      return;
    }
    const inCart = new Set((cart.items || []).map((i) => String(i.variant_id)));
    pendingAdds.forEach((at, variantId) => {
      if (Date.now() - at > PENDING_ADD_MS) return pendingAdds.delete(variantId);
      if (!inCart.has(variantId)) return;
      pendingAdds.delete(variantId);
      const card = cardForVariant(variantId);
      if (card) removeSavedItem(card);
    });
  }

  function cardForVariant(variantId) {
    const input = document.querySelector(`.${CONTAINER_CLASS} product-card form input[name="id"][value="${variantId}"]`);
    return input ? input.closest('product-card') : null;
  }

  ['cart:refresh', 'cart:change', 'toast:show', 'theme:loading:end'].forEach((ev) => {
    document.addEventListener(ev, () => setTimeout(settlePendingAdds, 300));
  });
})();

/* Share flow (wishlist-share atom) -- the header chrome
   (sections/saba-wishlist-header.liquid) places the trigger; this binds
   it to the Wishlist King SDK's own shareWishlist(), the vendor's official
   pattern. The SDK builds the share URL itself -- nothing is hand-constructed
   here. Feature-detected split (drawn desktop-drawer / mobile-native):
     - desktop (no navigator.share): open the 480px share drawer shipped in the
       same section fragment, populate its read-only field from the SDK's
       share result, and let its 'Copy Link' button + 'Share to' row do the
       copying/sharing. The drawer's own 2s copied-label swap is the only
       feedback (the trigger-label swap was removed with it -- no dual path).
     - mobile: keep the shipped SDK call (navigator.share native sheet); the
       trigger keeps the 2s copied-label fallback when the SDK reports a
       clipboard copy instead of a sheet.
   Delegated so it survives the chrome re-stamping above. */
(function () {
  const SHARE_DRAWER_ID = 'drawer-wishlist-share';
  const COPY_RESET_MS = 2000;

  function shareContext(btn) {
    const wk = window.WishlistKing;
    if (!wk || typeof wk.shareWishlist !== 'function') return null;
    const stashed = window.__sabaWishlist || {};
    const state = (wk.state && wk.state.wishlist) || {};
    const wishlistId = stashed.publicId || state.publicId;
    if (!wishlistId) return null;
    const wrap = btn.closest('.saba-wishlist-header__share-wrap');
    return {
      wk: wk,
      wishlistId: wishlistId,
      title: (wrap && wrap.dataset.shareTitle) || document.title,
      text: (wrap && wrap.dataset.shareMessage) || '',
      copiedLabel: (wrap && wrap.dataset.copiedLabel) || '',
    };
  }

  function swapLabel(el, text) {
    if (!el || !text) return;
    const label = el.querySelector('.saba-btn__label') || el;
    if (label.dataset.sabaCopyOriginal === undefined) {
      label.dataset.sabaCopyOriginal = label.textContent;
    }
    label.textContent = text;
    clearTimeout(el._sabaCopyTimer);
    el._sabaCopyTimer = setTimeout(function () {
      label.textContent = label.dataset.sabaCopyOriginal;
      delete label.dataset.sabaCopyOriginal;
    }, COPY_RESET_MS);
  }

  function shareUrlFromResult(result) {
    if (!result || typeof result !== 'object') return '';
    return result.url || result.shareUrl || result.link || result.share_url || '';
  }

  // Rewrites the drawer's inert social href templates with the SDK-built URL
  // and un-hides the row. Template shapes are the lineage share-buttons ones;
  // the URL value is always the SDK's, never assembled.
  function armShareRow(drawer, url, ctx) {
    const row = drawer.querySelector('.saba-wishlist-share__social-row');
    if (!row) return;
    const encoded = encodeURIComponent(url);
    const hrefs = {
      facebook: 'https://www.facebook.com/sharer.php?u=' + encoded,
      pinterest: 'https://pinterest.com/pin/create/button/?url=' + encoded,
      twitter:
        'https://twitter.com/share?' + (ctx.title ? 'text=' + encodeURIComponent(ctx.title) + '&' : '') + 'url=' + encoded,
      email:
        'mailto:?subject=' + encodeURIComponent(ctx.title || '') + '&body=' + encoded,
    };
    drawer.querySelectorAll('.saba-wishlist-share__socials a[data-network]').forEach(function (a) {
      if (hrefs[a.dataset.network]) a.setAttribute('href', hrefs[a.dataset.network]);
    });
    row.removeAttribute('aria-hidden');
  }

  function openShareDrawer(ctx) {
    const drawer = document.getElementById(SHARE_DRAWER_ID);
    if (!drawer || typeof drawer.show !== 'function') return false;
    drawer.show();
    ctx.wk
      .shareWishlist({ wishlistId: ctx.wishlistId, title: ctx.title, text: ctx.text })
      .then(function (result) {
        const url = shareUrlFromResult(result);
        const field = drawer.querySelector('.saba-wishlist-share__field .saba-input__field');
        if (url) {
          if (field) {
            field.value = url;
            const wrap = field.closest('.saba-input');
            if (wrap) wrap.classList.add('saba-input--has-value');
          }
          armShareRow(drawer, url, ctx);
        }
      })
      .catch(function () {});
    return true;
  }

  function copyShareUrl(copyBtn) {
    const drawer = copyBtn.closest('#' + SHARE_DRAWER_ID);
    const field = drawer && drawer.querySelector('.saba-wishlist-share__field .saba-input__field');
    const url = field && field.value;
    if (!url) return;
    const trigger = document.querySelector('.saba-wishlist-header__share');
    const ctx = shareContext(trigger || copyBtn);
    const copiedLabel = (ctx && ctx.copiedLabel) || 'Link copied';
    function done() {
      swapLabel(copyBtn, copiedLabel);
    }
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      navigator.clipboard.writeText(url).then(done).catch(function () {
        field.select();
        document.execCommand('copy');
        done();
      });
    } else {
      field.select();
      document.execCommand('copy');
      done();
    }
  }

  document.addEventListener('click', function (e) {
    const copyBtn = e.target.closest && e.target.closest('.saba-wishlist-share__copy');
    if (copyBtn) {
      copyShareUrl(copyBtn);
      return;
    }

    const btn = e.target.closest && e.target.closest('.saba-wishlist-header__share');
    if (!btn || btn.dataset.sabaShareBusy) return;
    const ctx = shareContext(btn);
    if (!ctx) return;
    btn.dataset.sabaShareBusy = '1';

    // Desktop drawer path -- the drawn 480px share drawer. Mobile keeps the
    // SDK's native-sheet call below (the drawer is a desktop-only artefact;
    // navigator.share presence is the capability split, not a UA sniff).
    if (typeof navigator.share !== 'function' && openShareDrawer(ctx)) {
      delete btn.dataset.sabaShareBusy;
      return;
    }

    ctx.wk
      .shareWishlist({ wishlistId: ctx.wishlistId, title: ctx.title, text: ctx.text })
      .then(function (result) {
        if (result && result.clipboard && ctx.copiedLabel) {
          swapLabel(btn, ctx.copiedLabel);
        }
      })
      .catch(function () {})
      .then(function () {
        delete btn.dataset.sabaShareBusy;
      });
  });
})();
